import type { SummaryKey } from "./summaries";

export type Project = {
  title: string;
  summaryKey: SummaryKey;
  technologies: string[];
  link?: string;
  repo?: string;
  timeline: string;
  impact: string;
  media?: Media[];
  tags: ProjectTags;
};

export type ProjectTags = {
  languages: string[];
  categories: string[];
  context: "research" | "personal" | "class";
};

export type ExperienceItem = {
  role: string;
  organization: string;
  timeline: string;
  details: string[];
};

export type HeroContent = {
  name: string;
  headline: string;
  bio: string;
  email: string;
};

export const hero: HeroContent = {
  name: "Computer Science Graduate Student",
  headline: "I craft vision-first XR experiences, real-time systems, and robust interfaces.",
  bio: "CS graduate student specializing in computer vision and XR blending VR/AR interaction design with GPU-aware pipelines, streaming telemetry, and well tested code.",
  email: "na4nn@umsystem.edu"
};

export type Media =
  | {
      type: "image";
      src: string;
      caption?: string;
    }
  | {
      type: "video";
      src: string;
      caption?: string;
    };

export const skills = [
  "C++ / C# / C",
  "Python",
  "Java",
  "Kotlin",
  "JavaScript / ReactJS / Angular",
  "Unity3D",
  "Unreal Engine",
  "WebGL",
  "Pytorch",
  "TensorFlow",
  "PHP / SQL",
  "Git",
  "Spring Boot",
  "UNIX",
  "AWS",
  "AJAX / Google API Services",
  "HTML / CSS",
  "Blender 2.79+",
  "ZBrush",
  "Nvidia Omniverse",
  "Autodesk Suite"
];

export const projects: Project[] = [
  {
    title: "XR WebSocket Proctoring & Multimodal Assistant",
    summaryKey: "xr-websocket-proctoring",
    technologies: ["Unity", "C#", "Python", "WebSockets", "NVIDIA CUDA"],
    link: "#",
    repo: "#",
    timeline: "2025-Current",
    impact:
      "Reduced instructor load in clean-room training by centralizing telemetry and enabling AI-driven guidance across multiple headsets.",
    tags: {
      languages: ["C#", "Python", "Javascript", "HTML"],
      categories: ["XR", "Systems", "Backend"],
      context: "research"
    },
    media: [
      {
        type: "video",
        src: "https://youtu.be/PmcYVX8ESVc",
        caption: "Proctor webpage demo showcasing Unity shader based instance segmentation and real time eye-gaze logging streaming from Meta Quest Pro HMD."
      },
      {
        type: "video",
        src: "https://youtu.be/yj5XBh7wDLE",
        caption: "Proctor webpage demo showcasing realtime instance segmentation on AR device webcam feed on Hololens 2 HMD."
      },
      {
        type: "video",
        src: "https://youtu.be/oeXZ7whPahs",
        caption: "Proctor webpage demo showcasing ASR prompted AI assistance and TTS response on Hololens 2 HMD."
      }
    ]
  },
  {
    title: "CNC & Lab Equipment Synthetic Data Generator",
    summaryKey: "cnc-synthetic-generator",
    technologies: ["Blender", "Python", "bpycv", "NumPy", "OpenCV"],
    link: "#",
    repo: "#",
    timeline: "2025-Current",
    impact:
      "Generated structured multi-view datasets with HDRI lighting, coverage constraints, and precise camera intrinsics, cutting manual labeling effort and improving equipment detection performance.",
    tags: {
      languages: ["Python", "Blender"],
      categories: ["Computer Vision", "Synthetic Data", "3D Graphics"],
      context: "research"
    },
    media: [
      {
        type: "image",
        src: "/media/000008.png",
        caption: "Example RGB for a CNC control panel."
      },
      {
        type: "image",
        src: "/media/000008_inst_bw.png",
        caption: "Example instance segmentation for a CNC control panel."
      },
      {
        type: "image",
        src: "/media/000000_depth_vis.png",
        caption: "Example depth visualization for a CNC control panel."
      }
    ]
  },
  {
    title: "LLM-Assisted Literature Triage Tool",
    summaryKey: "llm-lit-triage",
    technologies: [
      "Python",
      "sentence-transformers",
      "llama.cpp",
      "NumPy",
      "Zotero"
    ],
    link: "#",
    repo: "#",
    timeline: "2025",
    impact:
      "Automated first-pass relevance scoring that can handle thousands of papers, reducing manual screening time for the PRISMA systematic review and producing clean Zotero imports filtered by learned thresholds.",
    tags: {
      languages: ["Python"],
      categories: [
        "NLP",
        "Literature Mining",
        "Systematic Review",
        "Education Technology"
      ],
      context: "research"
    },
    media: [
    ]
  },

  {
    title: "Practical Pastures",
    summaryKey: "practical-pastures",
    technologies: ["Java", "Kotlin", "Mixin", "Neoforge"],
    link: "#",
    repo: "#",
    timeline: "2025-Current",
    impact:
      "Reduced instructor load in clean-room training by centralizing telemetry and enabling AI-driven guidance across multiple headsets.",
    tags: {
      languages: ["Java", "Kotlin"],
      categories: ["Mod Development"],
      context: "personal"
    },
    media: [
      {
        type: "video",
        src: "https://youtu.be/YALPVFA-DT4",
        caption: "Demo showcasing mod features."
      }
    ]
  },


  {
    title: "Project Showcase Hub",
    summaryKey: "project-showcase",
    technologies: ["React", "TypeScript", "Vite", "CSS Modules"],
    link: "#",
    repo: "#",
    timeline: "2025",
    impact: "Built reusable UI primitives and a content model that scales to dozens of projects.",
    tags: {
      languages: ["TypeScript"],
      categories: ["Frontend", "UI System"],
      context: "personal"
    },
    media: [
    ]
  },
  {
    title: "Drone Video Tracking in 3D Space",
    summaryKey: "drone-video-tracking",
    technologies: ["Python", "MMRotate", "ByteTrack", "Unreal Engine 5", "WebSockets"],
    link: "#",
    repo: "#",
    timeline: "2024",
    impact:
      "Bridged real drone footage and synthetic environments, enabling realistic moving cars and pedestrians for LiDAR simulation and future Gaussian splatting.",
    tags: {
      languages: ["Python", "C++"],
      categories: ["Tracking", "Simulation", "3D Reconstruction"],
      context: "research"
    },
    media: [
      {
        type: "video",
        src: "https://youtu.be/REpiGwmzp0E",
        caption: "Project showcase video."
      }
    ]
  },
  {
    title: "ExPose & SMPLX Video-to-3D Texturing Pipeline",
    summaryKey: "expose-smpx-pipeline",
    technologies: ["Python", "ExPose", "SMPLX", "Open3D", "trimesh", "pyrender"],
    link: "#",
    repo: "#",
    timeline: "2024",
    impact:
      "Demonstrated end-to-end reconstruction of textured human meshes from video, enabling future work on avatar creation, XR replay, and synthetic data generation.",
    tags: {
      languages: ["Python"],
      categories: ["Human Modeling", "3D Reconstruction"],
      context: "research"
    },
    media: [
      {
        type: "image",
        src: "/media/smplx.png",
        caption: "Untextured SMPLX mesh overlaid on the original video frame."
      },
      {
        type: "image",
        src: "/media/smplx_textured.png",
        caption: "Textured SMPLX mesh overlaid on the original video frame."
      }
    ]
  },
  {
    title: "Unreal Engine LiDAR Simulator & Synthetic Data Toolset",
    summaryKey: "unreal-lidar-toolset",
    technologies: ["Unreal Engine 5", "C++", "Blueprints", "Python", "Open3D"],
    link: "#",
    repo: "#",
    timeline: "2023–2024",
    impact:
      "Produced high-quality labeled LiDAR for indoor and outdoor scenes, enabling downstream research on detection, segmentation, and domain transfer.",
    tags: {
      languages: ["C++", "Python"],
      categories: ["Simulation", "Synthetic Data", "Autonomy"],
      context: "research"
    },
    media: [
      {
        type: "video",
        src: "https://youtu.be/1b_NzGAY7Js",
        caption: "LiDAR sensor visualization scanning St. Louis."
      },
      {
        type: "video",
        src: "https://youtu.be/6DVYHFvsK3I",
        caption: "LiDAR reconstruction visualization of St. Louis."
      }
    ]
  },
  {
  title: "Campus Pointcloud Optimization & Unity LODs",
  summaryKey: "campus-pointcloud-lod",
  technologies: ["Python", "Open3D", "Unity", "C#", "Blender"],
  link: "#",
  repo: "#",
  timeline: "2023",
  impact:
    "Reduced pointcount from tens of thousands of points to compact LODs, enabling real-time campus visualization without expensive rescanning.",
  tags: {
    languages: ["Python", "C#"],
    categories: ["Computer Vision", "3D Graphics", "Optimization"],
    context: "research"
  },
  media: [
  ]
}
];

export const experience: ExperienceItem[] = [
  {
    role: "Graduate Research Assistant for Dr. Fang Wang's XR Lab",
    organization: "University of Missouri",
    timeline: "2025 - Present",
    details: [
      "Lead graduate researcher for XR and AI development.",
      "Developed WebSocket based streaming and user metric analysis systems for XR."
    ]
  },
  {
    role: "Graduate Research Assistant for Dr. Ye Duans's Vision and Graphics Lab",
    organization: "University of Missouri",
    timeline: "2022 - 2025",
    details: [
      "Developed sophisticated data generation and visualization toolsets.",
      "Developed Unreal Engine based data generation and visualization platform.",
      "Primary developer for CAVE VR systems."
    ]
  },
  {
    role: "Teaching Assistant",
    organization: "CS 1050- Intro to Computer Science",
    timeline: "2023 - Present",
    details: [
      "Led weekly labs on covering the basics of C programming.",
      "Built grader scripts and sample solutions to standardize evaluation.",
      "Developed accurate AI ussage flagging scripts to detect academic dishonesty."
    ]
  },
  {
    role: "Co-Instructor",
    organization: "Computer Graphics Course",
    timeline: "2023 - 2024",
    details: [
      "Co-Instructed University of Missouri's Computer Graphics CS course.",
      "In charge of class lectures and course content.",
      "Taught WebGL and Unity development."
    ]
  },
  {
    role: "Software Enginerring Intern",
    organization: "Target",
    timeline: "2021 - 2022",
    details: [
      "Responsible for developing multiple internal facing web applications.",
      "Three total internships completed."
    ]
  }
];
